import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';

import { Input, BaseButton, Modal, ToolTip, useT } from '@repo/common';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/33px/icon_33px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';

const ActivityForm = ({ activity, setResumeData }) => {
  const t = useT();
  const [activities, setActivities] = useState([
    {
      id: uuidv4(),
      title: '',
      date: '',
      description: '',
    },
  ]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [hoverDragAndDropId, setHoverDragAndDropId] = useState(null);
  const [hoverDeleteId, setHoverDeleteId] = useState(null);
  const inputRefs = useRef({});

  useEffect(() => {
    if (activity && activity.length > 0) {
      setActivities(
        activity.map((ac) => ({
          ...ac,
          id: ac.id || uuidv4(),
        }))
      );
    }
  }, [activity]);

  const addActivity = () => {
    const newAc = {
      id: uuidv4(),
      title: '',
      date: '',
      description: '',
    };
    setActivities((prev) => [...prev, newAc]);

    setTimeout(() => {
      inputRefs.current[`title-${newAc.id}`]?.focus();
    }, 0);
  };

  const openConfirmModal = (id) => {
    setTargetId(id);
    setShowDeleteModal(true);
  };

  const updateActivities = (newActivities) => {
    setActivities(newActivities);
    setResumeData((prev) => ({ ...prev, activity: newActivities }));
  };

  const handleConfirmDelete = () => {
    const newActivities = activities.filter((ac) => ac.id !== targetId);
    updateActivities(newActivities);
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleInputChange = (id, field, value) => {
    const newActivities = activities.map((ac) =>
      ac.id === id ? { ...ac, [field]: value } : ac
    );
    setActivities(newActivities);

    setResumeData((prev) => ({
      ...prev,
      activity: newActivities,
    }));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(activities);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    updateActivities(items);
  };

  const didFocus = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (didFocus.current) return;
    if (activities.length === 0) return;

    const firstId = activities[0].id;
    setTimeout(() => {
      inputRefs.current[`title-${firstId}`]?.focus();
      didFocus.current = true;
    }, 0);
  }, [activities]);

  return (
    <div className='flex flex-col items-start w-full min-w-[460px] max-w-[950px] gap-6'>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId='activities' direction='vertical'>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className='flex flex-col gap-6 w-full'
            >
              {activities.map((ac, index) => (
                <Draggable key={ac.id} draggableId={ac.id} index={index}>
                  {(provided, snapshot) => {
                    const style = {
                      ...provided.draggableProps.style,
                      transform: provided.draggableProps.style?.transform
                        ? provided.draggableProps.style.transform.replace(
                            /translate\(([-\d.]+)px,/,
                            'translate(0px,'
                          )
                        : undefined,
                    };

                    if (!snapshot.isDragging) {
                      delete style.transition;
                    }

                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex w-full items-start`}
                      >
                        {/* Drag Handle */}
                        <div
                          {...provided.dragHandleProps}
                          className='relative flex-shrink-0 pt-2 cursor-grab active:cursor-grabbing text-grayscale-gray03 hover:text-icon-success active:text-icon-success'
                          onMouseEnter={() => setHoverDragAndDropId(ac.id)}
                          onMouseLeave={() => setHoverDragAndDropId(null)}
                        >
                          <DragAndDropIcon width={33} height={33} />
                          {hoverDragAndDropId === ac.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label={t('Click and drag to move', '클릭한 채 드래그하여 이동')}
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>

                        {/* Activity Form */}
                        <div className='flex-grow bg-grayscale-gray00 border border-grayscale-gray02 rounded-[8px] p-6 pb-[80px]'>
                          <div className='grid w-full grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-6'>
                            {/* Activity Title */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <Input
                                label={t('Activity title', '활동명')}
                                ref={(el) =>
                                  (inputRefs.current[`title-${ac.id}`] = el)
                                }
                                placeholder={t('e.g. YONSEI Korean Language Institute', '예: 연세대학교 한국어학당')}
                                value={ac.title}
                                onChange={(e) =>
                                  handleInputChange(
                                    ac.id,
                                    'title',
                                    e.target.value
                                  )
                                }
                                hideCount
                              />
                            </div>

                            {/* Date */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <Input
                                label={t('Date', '날짜')}
                                placeholder={t('e.g. Oct 2023', '예: 2023년 10월')}
                                value={ac.date}
                                onChange={(e) =>
                                  handleInputChange(
                                    ac.id,
                                    'date',
                                    e.target.value
                                  )
                                }
                                hideCount
                              />
                            </div>
                          </div>

                          {/* Description */}
                          <div className='flex flex-col mt-6'>
                            <span className='text-text-primary'>
                              {t('Description', '설명')}
                            </span>
                            <Input
                              as='textarea'
                              ref={(el) =>
                                (inputRefs.current[`description-${ac.id}`] = el)
                              }
                              placeholder={t(
                                'Briefly describe what you did, your role, and the outcome.\ne.g. Achieved TOPIK Level 5 through intensive Korean language training, with professional reading and writing proficiency',
                                '무엇을 했는지, 맡은 역할과 성과를 간단히 적어주세요.\n예: 집중 한국어 교육을 통해 TOPIK 5급을 취득하고, 업무 수준의 읽기·쓰기 능력을 갖춤'
                              )}
                              value={ac.description}
                              onChange={(e) =>
                                handleInputChange(
                                  ac.id,
                                  'description',
                                  e.target.value
                                )
                              }
                              maxLength={1000}
                              textareaRows={3}
                              textareaMinHeight='96px'
                              className='max-h-[60px]'
                            />
                          </div>
                        </div>

                        {/* Delete */}
                        <div className='flex-shrink-0 pt-1 relative'>
                          <button
                            className='w-10 h-10 flex justify-center items-center text-grayscale-gray04 hover:text-icon-success transition-colors'
                            onClick={() => openConfirmModal(ac.id)}
                            onMouseEnter={() => setHoverDeleteId(ac.id)}
                            onMouseLeave={() => setHoverDeleteId(null)}
                          >
                            <DeleteIcon width={24} height={24} />
                          </button>
                          {hoverDeleteId === ac.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label={t('Delete', '삭제')}
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className='w-full pl-[25px] pr-[40px]'>
        <BaseButton
          type='secondary'
          size='l'
          icon={<PlusIcon />}
          width='100%'
          onClick={addActivity}
          textWrapperClass='flex-none'
        >
          {t('Add one more Activity', '활동 추가하기')}
        </BaseButton>
      </div>

      {showDeleteModal && (
        <Modal
          isOpen
          onClickLeftBtn={handleCancelDelete}
          onClickRightBtn={handleConfirmDelete}
          title={t('Delete this activity entry?', '이 활동 항목을 삭제할까요?')}
          subText={t('This action can’t be undone.', '이 작업은 되돌릴 수 없습니다.')}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default ActivityForm;
